import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

export interface FilterConfig {
    [key: string]: any;
}

export interface PaginationConfig {
    page: number;
    pageSize: number;
}

export interface UrlStateConfig {
    filters?: FilterConfig;
    sort?: SortConfig;
    pagination?: PaginationConfig;
    preserveExisting?: boolean;
}

export const useUrlState = (config: UrlStateConfig = {}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize state from URL params
    const [filters, setFilters] = useState<FilterConfig>(config.filters || {});
    const [sort, setSort] = useState<SortConfig>(config.sort || { key: '', direction: 'asc' });
    const [pagination, setPagination] = useState<PaginationConfig>(
        config.pagination || { page: 1, pageSize: 25 }
    );

    // Initialize state from URL on mount
    useEffect(() => {
        const urlFilters: FilterConfig = {};
        const urlSort: SortConfig = { key: '', direction: 'asc' };
        const urlPagination: PaginationConfig = { page: 1, pageSize: 25 };

        // Parse filters from URL
        searchParams.forEach((value, key) => {
            if (key.startsWith('filter_')) {
                const filterKey = key.replace('filter_', '');
                urlFilters[filterKey] = value;
            }
        });

        // Parse sort from URL; when no sort in URL, use config default so list and detail stay in sync
        const sortKey = searchParams.get('sort');
        const sortDirection = searchParams.get('direction');
        if (sortKey) {
            urlSort.key = sortKey;
            urlSort.direction = (sortDirection as 'asc' | 'desc') || 'asc';
        } else if (config.sort?.key) {
            urlSort.key = config.sort.key;
            urlSort.direction = config.sort.direction;
        }

        // Parse pagination from URL
        const page = searchParams.get('page');
        const pageSize = searchParams.get('pageSize');
        if (page) urlPagination.page = parseInt(page);
        if (pageSize) urlPagination.pageSize = parseInt(pageSize);

        setFilters(urlFilters);
        setSort(urlSort);
        setPagination(urlPagination);
    }, [searchParams]);

    // Build URL search params from a full state object (avoids race when applying view in one shot)
    const stateToSearchParams = useCallback(
        (state: { filters: FilterConfig; sort: SortConfig; pagination: PaginationConfig }) => {
            const params = new URLSearchParams();
            Object.entries(state.filters).forEach(([key, value]) => {
                if (value !== '' && value !== null && value !== undefined) {
                    params.set(`filter_${key}`, String(value));
                }
            });
            if (state.sort.key) {
                params.set('sort', state.sort.key);
                params.set('direction', state.sort.direction);
            }
            if (state.pagination.page > 1) {
                params.set('page', state.pagination.page.toString());
            }
            if (state.pagination.pageSize !== 25) {
                params.set('pageSize', state.pagination.pageSize.toString());
            }
            return params;
        },
        []
    );

    // Update URL with new state (reads current URL; avoid when batching multiple updates)
    const updateUrl = useCallback(
        (newState: Partial<UrlStateConfig>) => {
            const newSearchParams = new URLSearchParams(searchParams);

            // Update filters
            if (newState.filters !== undefined) {
                searchParams.forEach((_, key) => {
                    if (key.startsWith('filter_')) {
                        newSearchParams.delete(key);
                    }
                });
                Object.entries(newState.filters).forEach(([key, value]) => {
                    if (value !== '' && value !== null && value !== undefined) {
                        newSearchParams.set(`filter_${key}`, String(value));
                    }
                });
            }

            // Update sort
            if (newState.sort !== undefined) {
                if (newState.sort.key) {
                    newSearchParams.set('sort', newState.sort.key);
                    newSearchParams.set('direction', newState.sort.direction);
                } else {
                    newSearchParams.delete('sort');
                    newSearchParams.delete('direction');
                }
            }

            // Update pagination
            if (newState.pagination !== undefined) {
                if (newState.pagination.page > 1) {
                    newSearchParams.set('page', newState.pagination.page.toString());
                } else {
                    newSearchParams.delete('page');
                }
                if (newState.pagination.pageSize !== 25) {
                    newSearchParams.set('pageSize', newState.pagination.pageSize.toString());
                } else {
                    newSearchParams.delete('pageSize');
                }
            }

            setSearchParams(newSearchParams);
        },
        [searchParams, setSearchParams]
    );

    /** Atomic replace of filters + sort + pagination and one URL update. Use when applying a view to avoid race. */
    const replaceState = useCallback(
        (next: Partial<UrlStateConfig>) => {
            const mergedFilters = next.filters !== undefined ? next.filters : filters;
            const mergedSort =
                next.sort !== undefined && next.sort?.key
                    ? next.sort
                    : sort.key
                      ? sort
                      : (config.sort as SortConfig) || { key: '', direction: 'asc' as const };
            const mergedPagination =
                next.pagination !== undefined ? { ...pagination, ...next.pagination } : pagination;

            setFilters(mergedFilters);
            setSort(mergedSort);
            setPagination(mergedPagination);
            setSearchParams(
                stateToSearchParams({
                    filters: mergedFilters,
                    sort: mergedSort,
                    pagination: mergedPagination,
                })
            );
        },
        [filters, sort, pagination, config.sort, stateToSearchParams, setSearchParams]
    );

    // Filter management
    const updateFilters = useCallback(
        (newFilters: FilterConfig) => {
            setFilters(newFilters);
            updateUrl({
                filters: newFilters,
                pagination: { page: 1, pageSize: pagination.pageSize },
            });
        },
        [pagination.pageSize, updateUrl]
    );

    const clearFilters = useCallback(() => {
        setFilters({});
        updateUrl({ filters: {} });
    }, [updateUrl]);

    // Sort management
    const updateSort = useCallback(
        (newSort: SortConfig) => {
            setSort(newSort);
            updateUrl({ sort: newSort });
        },
        [updateUrl]
    );

    const toggleSort = useCallback(
        (key: string) => {
            const newSort: SortConfig = {
                key,
                direction: sort.key === key && sort.direction === 'asc' ? 'desc' : 'asc',
            };
            updateSort(newSort);
        },
        [sort.key, sort.direction, updateSort]
    );

    // Pagination management
    const updatePagination = useCallback(
        (newPagination: Partial<PaginationConfig>) => {
            const updatedPagination = { ...pagination, ...newPagination };
            setPagination(updatedPagination);
            updateUrl({ pagination: updatedPagination });
        },
        [pagination, updateUrl]
    );

    const goToPage = useCallback(
        (page: number) => {
            updatePagination({ page });
        },
        [updatePagination]
    );

    const changePageSize = useCallback(
        (pageSize: number) => {
            updatePagination({ page: 1, pageSize }); // Reset to first page
        },
        [updatePagination]
    );

    // Reset all state
    const resetState = useCallback(() => {
        setFilters({});
        setSort({ key: '', direction: 'asc' });
        setPagination({ page: 1, pageSize: 25 });
        setSearchParams(new URLSearchParams());
    }, [setSearchParams]);

    // Get current URL state as object
    const getUrlState = useCallback(
        () => ({
            filters,
            sort,
            pagination,
            searchParams: Object.fromEntries(searchParams.entries()),
        }),
        [filters, sort, pagination, searchParams]
    );

    return {
        // State
        filters,
        sort,
        pagination,

        // Actions
        updateFilters,
        clearFilters,
        updateSort,
        toggleSort,
        updatePagination,
        goToPage,
        changePageSize,
        resetState,
        replaceState,

        // Utilities
        updateUrl,
        getUrlState,
        searchParams,
    };
};
